import { useCallback, useEffect, useRef, useState } from "react"
import * as amplitude from "@amplitude/analytics-react-native"
import { useCheckout } from "@stripe/react-stripe-js/checkout"
import { notifyBugsnag } from "@common/utils/initBugsnag"
import {
    PAYMENT_ELEMENT_TIMEOUT_MS,
    nextPaymentElementStatus,
} from "../newSubscriptionUtils"

/**
 * Surveille le formulaire de paiement Stripe monté dans la page (à appeler sous
 * un `CheckoutProvider`) : rend son
 * statut ({@link nextPaymentElementStatus}) et les deux rappels à brancher sur
 * `PaymentElement`. `active` : le formulaire est affiché (pas quand la famille
 * paie avec sa carte enregistrée).
 *
 * Chaque verdict part dans Amplitude — le navigateur y est déjà (propriété `os`) —,
 * et un échec part aussi dans Bugsnag : sans eux, une page qui tournait sans fin
 * ne laissait aucune trace.
 */
export const usePaymentElementWatch = ({ surface, active }) => {
    const [status, setStatus] = useState("loading")
    const statusRef = useRef("loading")
    const startedAtRef = useRef(null)

    const record = useCallback(
        (event, details = {}) => {
            const previous = statusRef.current
            const next = nextPaymentElementStatus(previous, event)
            if (next === previous) return
            statusRef.current = next
            setStatus(next)

            const properties = {
                surface,
                elapsed_ms: startedAtRef.current
                    ? Date.now() - startedAtRef.current
                    : null,
                ...details,
            }
            if (next === "ready") {
                amplitude.track("checkout.payment_element_ready", {
                    ...properties,
                    after_timeout: previous === "timeout",
                })
                return
            }
            amplitude.track("checkout.payment_element_failed", {
                ...properties,
                reason: next,
            })
            notifyBugsnag(
                new Error(
                    `Payment Element ${next} (${surface})${details.message ? `: ${details.message}` : ""}`
                )
            )
        },
        [surface]
    )

    useEffect(() => {
        if (!active || statusRef.current !== "loading") return
        startedAtRef.current = Date.now()
        const timer = setTimeout(
            () => record("timeout"),
            PAYMENT_ELEMENT_TIMEOUT_MS
        )
        return () => clearTimeout(timer)
    }, [active, record])

    // Stripe.js qui ne se charge pas du tout (script bloqué) remonte par le
    // contexte Checkout, pas par le formulaire.
    const checkoutState = useCheckout()
    useEffect(() => {
        if (checkoutState?.type === "error") {
            record("load_error", { message: checkoutState.error?.message })
        }
    }, [checkoutState?.type, record])

    const onReady = useCallback(() => record("ready"), [record])
    const onLoadError = useCallback(
        (event) => record("load_error", { message: event?.error?.message }),
        [record]
    )

    return { status, onReady, onLoadError }
}
